import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw,
  Save,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL;

const API = `${BACKEND_URL}/api`;

const ROLES = [
  {
    value: "graphics_admin",
    label: "Graphics Admin",
    description:
      "Graphics, project, user, and system administration.",
    cls:
      "bg-purple-500/20 text-purple-300 border-purple-500/40",
  },
  {
    value: "sales_manager",
    label: "Sales Ops Management",
    description:
      "Team dashboards, intake management, and Sales Ops workflow access.",
    cls:
      "bg-amber-500/20 text-amber-300 border-amber-500/40",
  },
  {
    value: "sales_rep",
    label: "Sales Ops",
    description:
      "Submit requests and work with assigned intake and projects.",
    cls:
      "bg-blue-500/20 text-blue-300 border-blue-500/40",
  },
  {
    value: "sales_viewer",
    label: "Viewer",
    description:
      "Read-only InkSight access.",
    cls:
      "bg-gray-500/20 text-gray-300 border-gray-500/40",
  },
];

const roleInfo = (role) =>
  ROLES.find(
    (item) => item.value === role,
  ) || ROLES[3];

const isMicrosoftAccount = (account) =>
  account.auth_method === "microsoft"
  || Boolean(account.microsoft_id)
  || Boolean(account.entra_object_id);

const roleSourceLabel = (account) => {
  switch (account.role_source) {
    case "inksight_admin_override":
      return "Managed in InkSight";
    case "entra_app_role":
      return "Synchronized from Entra";
    case "local_preprovisioned":
      return "Pre-provisioned in InkSight";
    case "local":
    case "local_admin_created":
      return "Local InkSight account";
    default:
      return account.role_source
        ? String(account.role_source)
            .replaceAll("_", " ")
        : "Not recorded";
  }
};

const formatDate = (value) => {
  if (!value) return "Not recorded";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
};

export const UsersPageV4 = () => {
  const {
    isAdmin,
    user: currentUser,
  } = useAuth();

  const [users, setUsers] = useState([]);
  const [draftRoles, setDraftRoles] =
    useState({});
  const [loading, setLoading] =
    useState(true);
  const [savingUserId, setSavingUserId] =
    useState(null);
  const [search, setSearch] =
    useState("");

  const fetchUsers = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${API}/users`,
      );

      const loaded = [
        ...(response.data || []),
      ].sort((left, right) =>
        String(
          left.full_name
          || left.email
          || left.username
          || "",
        ).localeCompare(
          String(
            right.full_name
            || right.email
            || right.username
            || "",
          ),
        ),
      );

      setUsers(loaded);

      setDraftRoles(
        Object.fromEntries(
          loaded.map((account) => [
            account.id,
            account.role || "sales_viewer",
          ]),
        ),
      );
    } catch (error) {
      toast.error(
        error.response?.data?.detail
        || "Failed to load users",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const visibleUsers = useMemo(() => {
    const term =
      search.trim().toLowerCase();

    if (!term) return users;

    return users.filter((account) =>
      [
        account.full_name,
        account.email,
        account.username,
        roleInfo(account.role).label,
        roleSourceLabel(account),
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(term),
      ),
    );
  }, [search, users]);

  const saveRole = async (account) => {
    const nextRole =
      draftRoles[account.id]
      || account.role
      || "sales_viewer";

    if (nextRole === account.role) {
      toast.info("No role change to save");
      return;
    }

    const name =
      account.full_name
      || account.email
      || account.username;

    const confirmed = window.confirm(
      `Change ${name} from `
      + `${roleInfo(account.role).label} to `
      + `${roleInfo(nextRole).label}?`,
    );

    if (!confirmed) {
      setDraftRoles((previous) => ({
        ...previous,
        [account.id]:
          account.role || "sales_viewer",
      }));

      return;
    }

    setSavingUserId(account.id);

    try {
      const response = await axios.put(
        `${API}/users/${account.id}/access-role`,
        { role: nextRole },
      );

      setUsers((previous) =>
        previous.map((item) =>
          item.id === account.id
            ? {
                ...item,
                role: nextRole,
                role_source:
                  response.data.role_source
                  || item.role_source,
                role_updated_at:
                  response.data.role_updated_at
                  || new Date().toISOString(),
                role_updated_by:
                  response.data.role_updated_by
                  || currentUser?.full_name,
              }
            : item,
        ),
      );

      toast.success(
        response.data.requires_new_login
          ? (
              "Role saved. The user must "
              + "sign out and back in."
            )
          : "Role saved.",
      );
    } catch (error) {
      setDraftRoles((previous) => ({
        ...previous,
        [account.id]:
          account.role || "sales_viewer",
      }));

      toast.error(
        error.response?.data?.detail
        || "Failed to update role",
      );
    } finally {
      setSavingUserId(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-300">
        Graphics Admin access required.
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 text-3xl font-bold">
            <UsersRound size={30} />
            User Roles
          </h1>

          <p className="mt-1 text-muted-foreground">
            Assign and manage InkSight roles.
          </p>
        </div>

        <button
          type="button"
          onClick={fetchUsers}
          className="flex items-center gap-2 rounded-lg bg-secondary px-4 py-2.5"
        >
          <RefreshCw size={17} />
          Refresh
        </button>
      </div>

      <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-4 text-sm text-blue-100">
        <div className="flex items-start gap-3">
          <ShieldCheck
            size={20}
            className="mt-0.5 shrink-0"
          />

          <div>
            <p className="font-semibold">
              InkSight role administration
            </p>

            <p className="mt-1 text-blue-100/80">
              A role saved here becomes the effective
              InkSight role. Microsoft users must sign
              out and back in before the change takes
              effect. You cannot change your own role.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {ROLES.map((role) => (
          <div
            key={role.value}
            className="rounded-xl border border-border bg-card p-4"
          >
            <span
              className={`inline-block rounded-full border px-2.5 py-1 text-xs font-semibold ${role.cls}`}
            >
              {role.label}
            </span>

            <p className="mt-3 text-sm text-muted-foreground">
              {role.description}
            </p>
          </div>
        ))}
      </div>

      <input
        type="search"
        value={search}
        onChange={(event) =>
          setSearch(event.target.value)
        }
        placeholder="Search users, emails, or roles..."
        className="w-full max-w-xl rounded-lg border border-border bg-secondary px-4 py-2.5"
      />

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="border-b border-border px-5 py-4">
          <h2 className="font-semibold">
            User Accounts
          </h2>
        </div>

        {loading ? (
          <div className="animate-pulse p-8 text-center text-muted-foreground">
            Loading users...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="px-4 py-3 text-left text-sm">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-sm">
                    Sign-in
                  </th>
                  <th className="px-4 py-3 text-left text-sm">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-sm">
                    Role Source
                  </th>
                  <th className="px-4 py-3 text-left text-sm">
                    Last Updated
                  </th>
                  <th className="px-4 py-3 text-left text-sm">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {visibleUsers.map((account) => {
                  const draftRole =
                    draftRoles[account.id]
                    || account.role
                    || "sales_viewer";

                  const info =
                    roleInfo(draftRole);

                  const isSelf =
                    account.id === currentUser?.id;

                  const changed =
                    draftRole !== account.role;

                  const saving =
                    savingUserId === account.id;

                  return (
                    <tr
                      key={account.id}
                      className="border-t border-border"
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium">
                          {account.full_name
                            || "No name"}
                        </p>

                        <p className="text-xs text-muted-foreground">
                          {account.email
                            || account.username}
                        </p>

                        {isSelf && (
                          <p className="mt-1 text-xs text-amber-300">
                            Current account
                          </p>
                        )}
                      </td>

                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {isMicrosoftAccount(account)
                          ? "Microsoft SSO"
                          : "Local"}
                      </td>

                      <td className="px-4 py-3">
                        <select
                          value={draftRole}
                          onChange={(event) =>
                            setDraftRoles(
                              (previous) => ({
                                ...previous,
                                [account.id]:
                                  event.target.value,
                              }),
                            )
                          }
                          disabled={isSelf || saving}
                          className={`rounded-lg border bg-background px-3 py-2 text-sm disabled:opacity-50 ${info.cls}`}
                        >
                          {ROLES.map((role) => (
                            <option
                              key={role.value}
                              value={role.value}
                              className="bg-card text-foreground"
                            >
                              {role.label}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {roleSourceLabel(account)}
                      </td>

                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        <p>
                          {formatDate(
                            account.role_updated_at
                            || account.role_synced_at,
                          )}
                        </p>

                        {account.role_updated_by && (
                          <p className="mt-1 text-xs">
                            By {account.role_updated_by}
                          </p>
                        )}
                      </td>

                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            saveRole(account)
                          }
                          disabled={
                            isSelf
                            || saving
                            || !changed
                          }
                          className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Save size={15} />

                          {saving
                            ? "Saving..."
                            : "Save"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {!visibleUsers.length && (
              <div className="p-8 text-center text-muted-foreground">
                No matching users found.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
